import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { submit } from 'redux-form'

import { Modal } from 'Components/Modal'
import { ModalBody } from 'Components/Modal/ModalBody'
import { ModalHeader } from 'Components/Modal/ModalHeader'
import { ModalTitle } from 'Components/Modal/ModalTitle'

import { AddConversionReportForm } from 'Containers/Form/Campaigns/AddConversionReport'
import { getCampaigns } from 'Reducers/Campaigns/actions'
import { getCountries } from 'Reducers/Countries/actions'

import { authorization } from 'Module/Auth/Auth'

import { validateRule } from 'Utils/Conversion'

import { CONVERSION_REPORT_TYPE } from '../../../../shared/constants'
import useConversionsUpload from '../../../Campaigns/Page/Conversions/hook/useConversionsUpload'
import { getConversionReportType } from '../../../Campaigns/Page/Conversions/gql'
import { ModalDescription } from '../../../../Components/Modal/ModalDescription'

const formName = 'add-conversion-report-form'

const AddConversionReportModal = props => {
  const [countryCode, setCountryCode] = useState(null)
  const [selectedCampaign, setSelectedCampaign] = useState(null)
  const dispatch = useDispatch()
  const campaigns = useSelector(state => state.campaigns.activeCampaignsList)
  const countries = useSelector(state => state.countries.countriesList)

  const withTimestamp = selectedCampaign
    ? selectedCampaign.conversionReportType ===
      CONVERSION_REPORT_TYPE.WITH_TIMESTAMP
    : false
  const {
    hideModal,
    startRunningValidation,
    handleConversionsUploadSuccess,
    handleConversionsUploadError,
  } = useConversionsUpload(withTimestamp)

  const {
    modalShow,
    modalCancelButtonHandler,
    initiallySelectedCampaign,
  } = props

  useEffect(() => {
    if (modalShow) {
      dispatch(getCampaigns({ statuses: 'ACTIVE' }))
      dispatch(getCountries())
    }
    const { countryCode } = authorization.getUser()
    setCountryCode(countryCode || 'FR')
  }, [])

  const modalCancelHandler = () => {
    modalCancelButtonHandler()
  }

  const modalConfirmHandler = () => {
    dispatch(submit(formName))
  }

  const addConversionReportFormSubmitHandler = async values => {
    if (!selectedCampaign) return

    modalCancelButtonHandler()
    startRunningValidation()

    const response = await getConversionReportType(selectedCampaign.id)
    const data = (await response.json()).data.campaign
    const metrics = (data.metrics ? data.metrics.nodes || [] : [])
      .sort((a, b) => (b.isPrimary ? 1 : -1))
      .map(metric => (metric.label ? metric.label : metric.name))
    const conversionsCount = data.conversionsCount || 0
    const isUSFormat = data.country.code === 'US'
    const callbacks = {
      onSuccess: () => {
        handleConversionsUploadSuccess()
      },
      onError: (errorType, handleConfirm, handleCancel, data = null) => {
        setTimeout(() => {
          handleConversionsUploadError(
            errorType,
            handleConfirm,
            handleCancel,
            data,
          )
        }, 1000)
      },
      stopLoading: () => {
        hideModal()
      },
    }

    validateRule({
      stage: 0,
      campaignId: selectedCampaign.id,
      reportFile: values.report[0],
      callbacks,
      withTimestamp,
      metrics,
      conversionsCount,
      isUSFormat,
      startRunningValidation,
    })
  }

  return (
    <Modal show={modalShow}>
      <ModalHeader>
        <ModalTitle>Import Conversion Report</ModalTitle>
        <ModalDescription>
          In order to upload report for Completed/Archived campaign, please
          proceed to that campaign's Conversions page
        </ModalDescription>
        <ModalDescription>
          <b>Please note:</b> After uploading, system checks are performed to
          ensure accuracy. It may take approximately 2 hours for the conversion
          data and any associated bonuses (if applicable) to be fully processed
          and reflected in the Brand Ambassador App.
        </ModalDescription>
      </ModalHeader>
      <ModalBody>
        <AddConversionReportForm
          initiallySelectedCampaign={initiallySelectedCampaign}
          campaigns={campaigns}
          countries={countries}
          userCountryCode={countryCode}
          form={formName}
          onSubmit={addConversionReportFormSubmitHandler}
          setSelectedCampaign={setSelectedCampaign}
          modalCancelHandler={modalCancelHandler}
          modalConfirmHandler={modalConfirmHandler}
        />
      </ModalBody>
    </Modal>
  )
}

export default AddConversionReportModal
